import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FeedPost from '../components/feed/FeedPost';
import api from '../lib/api';
import { DEFAULT_WEIGHT_KG } from '../lib/calories';
import { useToast } from '../components/common/Toast';
import { logger } from '../lib/logger';
import { 
  faPencilAlt,
  faMapMarkerAlt,
  faBullseye,
  faTrophy,
  faUsers,
  faEye,
  faEyeSlash,
  faShare,
  faUser,
  faPersonRunning,
  faBolt,
  faCalendar,
  faRuler,
  faClock,
  faTimes,
  faCamera,
  faSave,
  faSpinner,
  faArrowLeft,
  faUserPlus,
  faCheck,
  faWeightScale
} from '@fortawesome/free-solid-svg-icons';

// Helper functions
const parseDuration = (interval) => {
  if (!interval) return 0;
  const parts = interval.split(':');
  if (parts.length === 3) {
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
  }
  return 0;
};

const formatPace = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

const mapIconName = (iconName) => {
  const iconMap = {
    'faPersonRunning': 'running',
    'faBolt': 'bolt',
    'faTrophy': 'trophy',
    'faCalendar': 'calendar',
    'faMedal': 'trophy'
  };
  return iconMap[iconName] || 'trophy';
};

const achievementIconMap = {
  'running': faPersonRunning,
  'bolt': faBolt,
  'trophy': faTrophy,
  'calendar': faCalendar
};

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const toast = useToast();

  const isOwnProfile = !userId || userId === authUser?.id;
  const profileId = isOwnProfile ? authUser?.id : userId;

  const isMountedRef = useRef(true);
  const [isLoading, setIsLoading] = useState(true);

  const [user, setUser] = useState(isOwnProfile ? {
    id: authUser?.id || '',
    name: authUser?.name || '',
    username: authUser?.username || '',
    avatar: authUser?.avatar_url || null,
    bio: authUser?.bio || '',
    location: authUser?.location || '',
    joinedDate: ''
  } : null);

  const [stats, setStats] = useState({
    totalRuns: 0,
    totalDistance: 0,
    totalTime: '0h 0m',
    avgPace: '-',
    longestRun: 0
  });

  const [achievements, setAchievements] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
  const [followModalOpen, setFollowModalOpen] = useState(false);
  const [followModalType, setFollowModalType] = useState('followers');
  const [followModalUsers, setFollowModalUsers] = useState([]);
  const [followModalLoading, setFollowModalLoading] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    username: '',
    bio: '',
    location: '',
    avatar: null
  });
  const [privacy, setPrivacy] = useState({
    profileVisible: true,
    statsVisible: true,
    achievementsVisible: true,
    weightKg: DEFAULT_WEIGHT_KG
  });
  const [privacySaved, setPrivacySaved] = useState(false);

  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!profileId) return;

    try {
      const profile = await api.profiles.get(profileId);

      if (profile && isMountedRef.current) {
        const joinDate = new Date(profile.created_at);
        const formattedDate = joinDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

        setUser({
          id: profile.id,
          name: profile.display_name || profile.username || 'Runner',
          username: `@${profile.username}`,
          avatar: profile.avatar_url,
          bio: profile.bio || '',
          location: profile.location || '',
          joinedDate: formattedDate
        });
      }
    } catch (error) {
      logger.error('Error fetching profile:', error);
      if (!isOwnProfile) setUser(null);
    }
  }, [profileId, isOwnProfile]);

  const fetchStats = useCallback(async () => {
    if (!profileId) return;

    try {
      const userStats = await api.profiles.getStats(profileId);

      if (userStats && isMountedRef.current) {
        const totalSeconds = userStats.total_duration ? parseDuration(userStats.total_duration) : 0;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const avgPace = userStats.avg_pace_seconds ? formatPace(userStats.avg_pace_seconds) : '-';

        setStats({
          totalRuns: userStats.total_runs || 0,
          totalDistance: parseFloat(userStats.total_distance_km) || 0,
          totalTime: `${hours}h ${minutes}m`,
          avgPace: avgPace,
          longestRun: parseFloat(userStats.longest_run_km) || 0
        });
      }
    } catch (error) {
      logger.warn('Error fetching stats (non-blocking):', error);
    }
  }, [profileId]);

  const fetchAchievements = useCallback(async () => {
    if (!profileId) return;

    try {
      const data = await api.profiles.getAchievements(profileId, 5);

      if (isMountedRef.current) {
        const formattedAchievements = (data || []).map(ua => ({
          id: ua.achievement?.id,
          title: ua.achievement?.title || 'Achievement',
          icon: mapIconName(ua.achievement?.icon),
          date: new Date(ua.earned_at).toLocaleDateString(),
          description: ua.achievement?.description || ''
        })).filter(a => a.id);

        setAchievements(formattedAchievements);
      }
    } catch (error) {
      logger.warn('Error fetching achievements (non-blocking):', error);
    }
  }, [profileId]);

  const fetchUserPosts = useCallback(async () => {
    if (!profileId) return;

    try {
      const posts = await api.posts.list(0, 50, profileId);

      if (isMountedRef.current) {
        const formattedPosts = (posts || []).map(post => ({
          id: post.id,
          type: post.type,
          title: post.title || '',
          content: post.content,
          distance: post.run?.distance_km?.toString() || '',
          time: post.run?.duration || '',
          pace: post.run?.pace || '',
          timestamp: formatTimeAgo(post.created_at),
          user: {
            id: post.user?.id,
            name: post.user?.display_name || post.user?.username || 'User',
            avatar: post.user?.avatar_url
          },
          likesCount: post.likes?.length || 0,
          isLiked: post.likes?.some(like => like.user_id === authUser?.id) || false,
          comments: (post.comments || []).map(c => ({
            id: c.id,
            user: c.user?.display_name || c.user?.username || 'User',
            avatar: c.user?.avatar_url || null,
            text: c.content,
            timestamp: formatTimeAgo(c.created_at)
          }))
        }));

        setUserPosts(formattedPosts);
      }
    } catch (error) {
      logger.error('Error fetching posts:', error);
    }
  }, [profileId, authUser?.id]);

  const fetchFollowCounts = useCallback(async () => {
    if (!profileId) return;

    try {
      const counts = await api.social.getCounts(profileId);

      if (isMountedRef.current) {
        setFollowCounts({
          followers: counts.followers || 0,
          following: counts.following || 0
        });
      }
    } catch (error) {
      logger.warn('Error fetching follow counts (non-blocking):', error);
    }
  }, [profileId]);

  const checkIsFollowing = useCallback(async () => {
    if (!profileId || !authUser?.id || isOwnProfile) return;

    try {
      const following = await api.social.isFollowing(profileId);

      if (isMountedRef.current) {
        setIsFollowing(following);
      }
    } catch (error) {
      logger.warn('Error checking follow status:', error);
    }
  }, [profileId, authUser?.id, isOwnProfile]);

  useEffect(() => {
    isMountedRef.current = true;

    const loadData = async () => {
      if (!profileId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const fetches = [
        fetchProfile(),
        fetchStats(),
        fetchAchievements(),
        fetchUserPosts(),
        fetchFollowCounts()
      ];

      if (!isOwnProfile) {
        fetches.push(checkIsFollowing());
      }

      await Promise.allSettled(fetches);

      if (isMountedRef.current) {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      isMountedRef.current = false;
    };
  }, [profileId, isOwnProfile, fetchProfile, fetchStats, fetchAchievements, fetchUserPosts, fetchFollowCounts, checkIsFollowing]);

  useEffect(() => {
    if (!isOwnProfile || !authUser?.id) return;

    const fetchPrivacySettings = async () => {
      try {
        const data = await api.profiles.getSettings(authUser.id);

        if (data) {
          setPrivacy({
            profileVisible: data.profile_visible ?? true,
            statsVisible: data.stats_visible ?? true,
            achievementsVisible: data.achievements_visible ?? true,
            weightKg: data.weight_kg ?? DEFAULT_WEIGHT_KG
          });
        }
      } catch (error) {
        logger.warn('Error fetching privacy settings (non-blocking):', error);
      }
    };

    fetchPrivacySettings();
  }, [isOwnProfile, authUser?.id]);

  useEffect(() => {
    if (isEditModalOpen || followModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isEditModalOpen, followModalOpen]);

  const handleUpdatePost = (updatedPost) => {
    setUserPosts(posts =>
      posts.map(post => post.id === updatedPost.id ? updatedPost : post)
    );
  };

  const openFollowModal = async (type) => {
    setFollowModalType(type);
    setFollowModalOpen(true);
    setFollowModalLoading(true);
    setFollowModalUsers([]);

    try {
      if (type === 'followers') {
        const data = await api.social.getFollowers(profileId);

        const users = (data || []).map(f => ({
          id: f.follower?.id,
          name: f.follower?.display_name || f.follower?.username || 'User',
          username: f.follower?.username,
          avatar: f.follower?.avatar_url
        })).filter(u => u.id);

        setFollowModalUsers(users);
      } else {
        const data = await api.social.getFollowing(profileId);

        const users = (data || []).map(f => ({
          id: f.following?.id,
          name: f.following?.display_name || f.following?.username || 'User',
          username: f.following?.username,
          avatar: f.following?.avatar_url
        })).filter(u => u.id);

        setFollowModalUsers(users);
      }
    } catch (error) {
      logger.error('Error fetching follow list:', error);
    } finally {
      setFollowModalLoading(false);
    }
  };

  const openEditModal = () => {
    setEditForm({
      name: user.name,
      username: user.username,
      bio: user.bio,
      location: user.location,
      avatar: user.avatar
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditForm(prev => ({ ...prev, avatar: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    if (!authUser?.id) return;

    const formattedUsername = editForm.username.startsWith('@')
      ? editForm.username.slice(1)
      : editForm.username;

    try {
      await api.profiles.update(authUser.id, {
        display_name: editForm.name,
        username: formattedUsername,
        bio: editForm.bio,
        location: editForm.location,
        avatar_url: editForm.avatar,
      });

      setUser(prev => ({
        ...prev,
        name: editForm.name,
        username: `@${formattedUsername}`,
        bio: editForm.bio,
        location: editForm.location,
        avatar: editForm.avatar
      }));

      closeEditModal();
      toast.success('Profile saved successfully!');
    } catch (error) {
      logger.error('Error saving profile:', error);
      toast.error('Failed to save profile. Please try again.');
    }
  };

  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/user/${user.id}`;
    const shareData = {
      title: `${user.name}'s Running Profile`,
      text: `Check out ${user.name}'s running profile on ZoneRuns!`,
      url: profileUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(profileUrl);
        toast.success('Profile link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(profileUrl);
          toast.success('Profile link copied to clipboard!');
        } catch {
          toast.error('Failed to share profile');
        }
      }
    }
  };

  const togglePrivacySetting = (setting) => {
    setPrivacy(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
    setPrivacySaved(false);
  };

  const handleSavePrivacy = async () => {
    if (!authUser?.id) return;

    try {
      await api.profiles.updateSettings(authUser.id, {
        profile_visible: privacy.profileVisible,
        stats_visible: privacy.statsVisible,
        achievements_visible: privacy.achievementsVisible,
        weight_kg: parseFloat(privacy.weightKg) || DEFAULT_WEIGHT_KG,
      });

      setPrivacySaved(true);
      setTimeout(() => setPrivacySaved(false), 2000);
      toast.success('Privacy settings saved!');
    } catch (error) {
      logger.error('Error saving privacy settings:', error);
      toast.error('Failed to save settings. Please try again.');
    }
  };

  const handleFollowToggle = async () => {
    if (!authUser?.id || !profileId || isFollowLoading) return;

    setIsFollowLoading(true);

    try {
      if (isFollowing) {
        await api.social.unfollow(profileId);
        setIsFollowing(false);
        setFollowCounts(prev => ({ ...prev, followers: prev.followers - 1 }));
      } else {
        await api.social.follow(profileId);
        setIsFollowing(true);
        setFollowCounts(prev => ({ ...prev, followers: prev.followers + 1 }));
      }
    } catch (error) {
      logger.error('Error toggling follow:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 max-w-4xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-primary-pink animate-spin mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!isOwnProfile && !user) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
          Go Back
        </button>
        <div className="scrapbook-container text-center py-12">
          <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">User not found</h3>
          <p className="text-gray-600">This user doesn't exist or their profile is private.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {!isOwnProfile && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
          Go Back
        </button>
      )}

      <div className="scrapbook-container mb-6">
        <div className="flex flex-row items-start gap-8">
          <div className="flex-shrink-0">
            <div className="w-28 h-28 rounded-full bg-gradient-vibe flex items-center justify-center text-4xl text-white font-bold shadow-lg overflow-hidden ring-4 ring-white">
              {user.avatar ? (
                <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <FontAwesomeIcon icon={faUser} className="text-white" />
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-heading font-bold text-gray-900 mb-0.5">
              {user.name}
            </h1>

            <p className="text-secondary-blue font-medium text-sm mb-3">{user.username}</p>

            <div className="flex items-center gap-4 mb-3">
              <button
                onClick={() => openFollowModal('followers')}
                className="flex items-center gap-1.5 hover:bg-gray-50 px-2 py-1 -ml-2 rounded-lg transition-colors"
              >
                <span className="font-heading font-bold text-gray-900">{followCounts.followers}</span>
                <span className="text-gray-500 text-sm">Followers</span>
              </button>
              <button
                onClick={() => openFollowModal('following')}
                className="flex items-center gap-1.5 hover:bg-gray-50 px-2 py-1 rounded-lg transition-colors"
              >
                <span className="font-heading font-bold text-gray-900">{followCounts.following}</span>
                <span className="text-gray-500 text-sm">Following</span>
              </button>
            </div>

            {user.bio && (
              <p className="text-gray-700 font-body text-sm mb-3 max-w-md leading-relaxed">
                {user.bio}
              </p>
            )}

            <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
              {user.location && (
                <span className="flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="w-3.5 h-3.5 text-gray-400" />
                  {user.location}
                </span>
              )}
              {user.location && user.joinedDate && (
                <span className="text-gray-300">&bull;</span>
              )}
              {user.joinedDate && (
                <span>Joined {user.joinedDate}</span>
              )}
            </div>

            <div className="flex gap-2">
              {isOwnProfile ? (
                <>
                  <button
                    onClick={handleShareProfile}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-vibe text-white text-sm font-semibold rounded-full shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                  >
                    <FontAwesomeIcon icon={faShare} className="w-3.5 h-3.5" />
                    Share Profile
                  </button>

                  <button
                    onClick={openEditModal}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-full border border-gray-300 hover:bg-gray-50 hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    <FontAwesomeIcon icon={faPencilAlt} className="w-3.5 h-3.5" />
                    Edit Profile
                  </button>
                </>
              ) : (
                <button
                  onClick={handleFollowToggle}
                  disabled={isFollowLoading}
                  className={`flex items-center gap-2 px-6 py-2 font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed ${
                    isFollowing
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-gradient-vibe text-white'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={isFollowLoading ? faSpinner : (isFollowing ? faCheck : faUserPlus)}
                    className={`w-4 h-4 ${isFollowLoading ? 'animate-spin' : ''}`}
                  />
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Runs', value: stats.totalRuns, icon: faPersonRunning },
          { label: 'Distance', value: `${stats.totalDistance}km`, icon: faRuler },
          { label: 'Time', value: stats.totalTime, icon: faClock },
          { label: 'Avg Pace', value: stats.avgPace, icon: faBullseye }
        ].map((stat) => (
          <div key={stat.label} className="scrapbook-container text-center">
            <div className="text-2xl mb-2 text-primary-pink">
              <FontAwesomeIcon icon={stat.icon} />
            </div>
            <div className="font-heading font-bold text-xl text-gray-900 mb-1">
              {stat.value}
            </div>
            <div className="text-xs text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>

      {isOwnProfile ? (
        <div className="grid grid-cols-2 gap-6">
          <div className="scrapbook-container">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faTrophy} className="w-5 h-5 text-primary-pink" />
              <h2 className="font-heading font-semibold text-lg">Recent Achievements</h2>
            </div>

            <div className="space-y-3 max-h-64 overflow-y-auto">
              {achievements.slice(0, 5).map((achievement) => {
                const icon = achievementIconMap[achievement.icon] || faTrophy;

                return (
                  <div
                    key={achievement.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gradient-vibe-subtle hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-vibe flex items-center justify-center">
                      <FontAwesomeIcon icon={icon} className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{achievement.title}</h3>
                      <p className="text-sm text-gray-600">{achievement.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{achievement.date}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="scrapbook-container">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faUsers} className="w-5 h-5 text-secondary-blue" />
              <h2 className="font-heading font-semibold text-lg">Settings</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <FontAwesomeIcon icon={faWeightScale} className="w-4 h-4 text-primary-pink" />
                    Weight
                  </h3>
                  <p className="text-sm text-gray-600">Used for calorie estimation</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="30"
                    max="300"
                    step="0.5"
                    value={privacy.weightKg}
                    onChange={(e) => {
                      setPrivacy(prev => ({ ...prev, weightKg: e.target.value }));
                      setPrivacySaved(false);
                    }}
                    className="w-20 px-3 py-1.5 text-right border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all"
                  />
                  <span className="text-sm text-gray-500 font-medium">kg</span>
                </div>
              </div>

              {[
                { key: 'profileVisible', label: 'Profile Visibility', desc: 'Others can view your profile' },
                { key: 'statsVisible', label: 'Statistics Sharing', desc: 'Share your running stats' },
                { key: 'achievementsVisible', label: 'Achievements', desc: 'Display your achievements' }
              ].map((setting) => (
                <div
                  key={setting.key}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{setting.label}</h3>
                    <p className="text-sm text-gray-600">{setting.desc}</p>
                  </div>

                  <button
                    onClick={() => togglePrivacySetting(setting.key)}
                    className={`p-2 rounded-xl transition-all hover:scale-[1.05] active:scale-[0.95] ${
                      privacy[setting.key]
                        ? 'bg-secondary-blue text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {privacy[setting.key] ? (
                      <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                    ) : (
                      <FontAwesomeIcon icon={faEyeSlash} className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={handleSavePrivacy}
              className={`w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 font-medium rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] ${
                privacySaved
                  ? 'bg-green-500 text-white'
                  : 'bg-secondary-blue text-white hover:bg-blue-600'
              }`}
            >
              <FontAwesomeIcon icon={faSave} className="w-4 h-4" />
              {privacySaved ? 'Saved!' : 'Save Settings'}
            </button>
          </div>
        </div>
      ) : (
        achievements.length > 0 && (
          <div className="scrapbook-container mb-6">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faTrophy} className="w-5 h-5 text-primary-pink" />
              <h2 className="font-heading font-semibold text-lg">Recent Achievements</h2>
            </div>

            <div className="flex flex-wrap gap-2">
              {achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className="px-3 py-2 rounded-xl bg-gradient-vibe-subtle text-sm font-medium text-gray-700"
                >
                  {achievement.title}
                </div>
              ))}
            </div>
          </div>
        )
      )}

      <div className="mt-6">
        <div className="flex items-center gap-2 mb-6">
          <FontAwesomeIcon icon={faPersonRunning} className="w-5 h-5 text-primary-pink" />
          <h2 className="font-heading font-semibold text-lg">
            {isOwnProfile ? 'My Posts' : `${user.name}'s Posts`}
          </h2>
          <span className="text-sm text-gray-500 ml-2">({userPosts.length})</span>
        </div>

        <div className="space-y-6">
          {userPosts.length > 0 ? (
            userPosts.map((post) => (
              <div key={post.id}>
                <FeedPost post={post} onUpdatePost={handleUpdatePost} />
              </div>
            ))
          ) : (
            <div className="scrapbook-container text-center py-12">
              <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">No posts yet</h3>
              <p className="text-gray-600">
                {isOwnProfile
                  ? 'Share your first run to get started!'
                  : `${user.name} hasn't shared any runs yet.`}
              </p>
            </div>
          )}
        </div>
      </div>

      {isOwnProfile && isEditModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeEditModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-bold text-gray-900">Edit Profile</h2>
              <button
                onClick={closeEditModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:scale-[1.1] active:scale-[0.9] transition-all"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gradient-vibe flex items-center justify-center overflow-hidden shadow-lg">
                    {editForm.avatar ? (
                      <img src={editForm.avatar} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <FontAwesomeIcon icon={faUser} className="text-white text-3xl" />
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary-pink rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-pink-600 transition-colors">
                    <FontAwesomeIcon icon={faCamera} className="text-white w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">@</span>
                  <input
                    type="text"
                    name="username"
                    value={editForm.username.replace(/^@/, '')}
                    onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value.replace(/^@/, '') }))}
                    className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  name="bio"
                  value={editForm.bio}
                  onChange={handleEditFormChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={editForm.location}
                  onChange={handleEditFormChange}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closeEditModal}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                className="flex-1 px-4 py-2 bg-gradient-vibe text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {followModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setFollowModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-heading font-bold text-gray-900">
                {followModalType === 'followers' ? 'Followers' : 'Following'}
              </h2>
              <button
                onClick={() => setFollowModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:scale-[1.1] active:scale-[0.9] transition-all"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {followModalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-primary-pink animate-spin" />
                </div>
              ) : followModalUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {isOwnProfile
                    ? (followModalType === 'followers'
                      ? "You don't have any followers yet."
                      : "You're not following anyone yet.")
                    : (followModalType === 'followers'
                      ? `${user.name} doesn't have any followers yet.`
                      : `${user.name} isn't following anyone yet.`)}
                </div>
              ) : (
                <div className="space-y-3">
                  {followModalUsers.map((followUser) => (
                    <Link
                      key={followUser.id}
                      to={followUser.id === authUser?.id ? '/profile' : `/user/${followUser.id}`}
                      onClick={() => setFollowModalOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-vibe flex items-center justify-center overflow-hidden">
                        {followUser.avatar ? (
                          <img src={followUser.avatar} alt={followUser.name} className="w-full h-full object-cover" />
                        ) : (
                          <FontAwesomeIcon icon={faUser} className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{followUser.name}</p>
                        <p className="text-sm text-gray-500">@{followUser.username}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
